import { useState } from "react";
import type { TemplateField } from "@/types/activity";
import { generateContentExample } from "@/lib/contentExampleGenerator";
import { generateContentSchema } from "@/lib/contentSchemaGenerator";
import DynamicCardFields from "@/components/activities/DynamicCardFields";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  code: string;
  label: string;
  fields: TemplateField[];
  contentSchema: Record<string, unknown> | null;
}

export default function TemplatePreviewDialog({
  open,
  onOpenChange,
  code,
  label,
  fields,
  contentSchema,
}: Props) {
  const [copiedTab, setCopiedTab] = useState<string | null>(null);

  const example = generateContentExample(fields);
  const schema = contentSchema ?? generateContentSchema(fields);
  const exampleJson = JSON.stringify(example, null, 2);
  const schemaJson = JSON.stringify(schema, null, 2);

  const copy = async (text: string, tab: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedTab(tab);
    toast.success("Copié dans le presse-papier");
    setTimeout(() => setCopiedTab(null), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-mono">{code}</DialogTitle>
          <DialogDescription>{label}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="example" className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <TabsList className="flex-shrink-0">
            <TabsTrigger value="example">Exemple content</TabsTrigger>
            <TabsTrigger value="schema">JSON Schema</TabsTrigger>
            <TabsTrigger value="preview">Prévisualisation</TabsTrigger>
          </TabsList>

          <TabsContent value="example" className="mt-2 flex flex-col gap-2 overflow-hidden flex-1 data-[state=inactive]:hidden">
            <div className="flex justify-end flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy(exampleJson, "example")}
              >
                {copiedTab === "example" ? (
                  <Check className="mr-2 h-3 w-3" />
                ) : (
                  <Copy className="mr-2 h-3 w-3" />
                )}
                Copier
              </Button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto rounded-md border bg-muted/50">
              <pre className="p-4 text-sm font-mono whitespace-pre-wrap break-words text-foreground">
                {exampleJson}
              </pre>
            </div>
          </TabsContent>

          <TabsContent value="schema" className="mt-2 flex flex-col gap-2 overflow-hidden flex-1 data-[state=inactive]:hidden">
            <div className="flex justify-end flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy(schemaJson, "schema")}
              >
                {copiedTab === "schema" ? (
                  <Check className="mr-2 h-3 w-3" />
                ) : (
                  <Copy className="mr-2 h-3 w-3" />
                )}
                Copier
              </Button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto rounded-md border bg-muted/50">
              <pre className="p-4 text-sm font-mono whitespace-pre-wrap break-words text-foreground">
                {schemaJson}
              </pre>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="mt-2 flex flex-col overflow-hidden flex-1 data-[state=inactive]:hidden">
            <div className="flex-1 min-h-0 overflow-auto rounded-md border bg-background p-4">
              {fields.length > 0 ? (
                <DynamicCardFields
                  fields={fields}
                  values={example as Record<string, unknown>}
                  onChange={() => {}}
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  Aucun champ défini pour ce template.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
